import {
  Block as BlockIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { apiFetch } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import OtpInput from "../components/OtpInput";

// API types
interface ApiUserCab { plateNumber: string; capacity: number; status: "available" | "busy" | "inactive" | string; make?: string | null; model?: string | null; year?: number | null; color?: string | null }
interface ApiUser {
  id: string;
  name: string;
  email: string;
  role: "rider" | "driver" | "admin" | string;
  status: string; // 'active' | 'inactive' etc.
  createdAt: string;
  cab?: ApiUserCab | null;
}
interface ApiResponse<T> { success: boolean; data: T; pagination?: { page: number; limit: number; total: number; totalPages: number } }

// UI row
interface UIDriverRow {
  id: string;
  name: string;
  email: string;
  driverStatus: "offline" | "online" | "in_trip" | "inactive";
  cabPlate: string;
  cabCapacity: string;
  cabStatus: "available" | "busy" | "inactive" | "-" | string;
  cabMake: string;
  cabModel: string;
  cabYear: string;
  cabColor: string;
}

interface DriverReportStats {
  totalTrips: number;
  completedTrips: number;
  cancelledTrips: number;
  ongoingTrips: number;
  totalRiders: number;
  averageRidersPerTrip: number;
  averageOccupancy: number;
  reviewCount: number;
  averageRating: number | null;
  firstTripDate: string | null;
  lastTripDate: string | null;
}

interface DriverReportTrip {
  id: string;
  state: string;
  serviceDate: string;
  startLocation: string;
  startLocationName?: string | null;
  endLocation?: string | null;
  endLocationName?: string | null;
  riderCount: number;
}

interface DriverReportReview {
  id: string;
  rating: number;
  comment?: string | null;
  createdAt: string;
  giver?: {
    id: string;
    name: string;
    email: string;
  } | null;
}

interface DriverReportData {
  driver: {
    id: string;
    name: string;
    email: string;
    status: string;
    createdAt: string;
  };
  cab: {
    id?: string;
    plateNumber: string;
    capacity: number | null;
    status?: string;
    make?: string | null;
    model?: string | null;
    year?: number | null;
    color?: string | null;
  } | null;
  stats: DriverReportStats;
  recentTrips: DriverReportTrip[];
  reviews: DriverReportReview[];
  allTrips: DriverReportTrip[];
  
  allReviews: DriverReportReview[];
}

const Users: React.FC = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  const [rows, setRows] = useState<UIDriverRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<DriverReportData | null>(null);
  const [reportTarget, setReportTarget] = useState<UIDriverRow | null>(null);

  // Add Driver (OTP-protected) dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [addOtpRequested, setAddOtpRequested] = useState(false);
  const [addOtp, setAddOtp] = useState("");
  const [addRequestingOtp, setAddRequestingOtp] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    licensePlate: "",
    capacity: "" as number | string,
    make: "",
    model: "",
    year: "" as number | string,
    color: "",
  });
  const [addOtpCooldown, setAddOtpCooldown] = useState(0);

  // Add Riders (single / bulk) OTP-protected dialog state
  const [ridersOpen, setRidersOpen] = useState(false);
  const [ridersMode, setRidersMode] = useState<'single' | 'bulk'>('single');
  const [ridersOtpRequested, setRidersOtpRequested] = useState(false);
  const [ridersOtp, setRidersOtp] = useState("");
  const [ridersOtpCooldown, setRidersOtpCooldown] = useState(0);
  const [ridersRequestingOtp, setRidersRequestingOtp] = useState(false);
  const [ridersSubmitting, setRidersSubmitting] = useState(false);
  const [ridersError, setRidersError] = useState<string | null>(null);
  const [riderSingleForm, setRiderSingleForm] = useState({ name: "", email: "" });
  const [riderBulkForm, setRiderBulkForm] = useState({ namesText: "", emailsText: "" });

  // Cooldown countdown for resend OTP
  useEffect(() => {
    if (addOtpCooldown <= 0) return;
    const t = setTimeout(() => setAddOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [addOtpCooldown]);

  // Cooldown timer for riders OTP resend
  useEffect(() => {
    if (ridersOtpCooldown <= 0) return;
    const t = setTimeout(() => setRidersOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [ridersOtpCooldown]);

  const formatDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "-";

  const formatDateTime = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "-";

  const statCard = (label: string, value: React.ReactNode, key?: string) => (
    <Box
      key={key || label}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );

  const closeReportDialog = () => {
    if (reportLoading) return;
    setReportOpen(false);
    setReportError(null);
    setReportData(null);
    setReportTarget(null);
  };

  const handleViewDriverReport = async (driver: UIDriverRow) => {
    if (!token) {
      setReportError("Authentication required.");
      setReportOpen(true);
      return;
    }
    setReportTarget(driver);
    setReportOpen(true);
    setReportLoading(true);
    setReportError(null);
    setReportData(null);
    try {
      const res = await apiFetch(`/admin/drivers/${driver.id}/report`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (json.success && json.data) {
        setReportData(json.data as DriverReportData);
      } else {
        setReportError(json.error || "Failed to load driver report.");
      }
    } catch (error) {
      setReportError("Network error. Please try again.");
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadDriverReport = () => {
    if (!reportData) return;

    const wb = XLSX.utils.book_new();
    const safeName = reportData.driver.name
      ? reportData.driver.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()
      : "driver";
    const generatedOn = new Date().toLocaleString();

    const summarySheet: (string | number)[][] = [
      ["Driver Report"],
      ["Generated On", generatedOn],
      [""],
      ["Driver Information"],
      ["Name", reportData.driver.name],
      ["Email", reportData.driver.email],
      ["Status", reportData.driver.status],
      ["Joined", formatDate(reportData.driver.createdAt)],
      [""],
      ["Cab Information"],
    ];

    if (reportData.cab) {
      summarySheet.push(
        ["Plate Number", reportData.cab.plateNumber],
        ["Capacity", reportData.cab.capacity ?? "N/A"],
        ["Status", reportData.cab.status ?? "N/A"],
        [
          "Vehicle",
          [reportData.cab.make, reportData.cab.model].filter(Boolean).join(" ") || "N/A",
        ],
        [
          "Additional Details",
          [reportData.cab.year, reportData.cab.color].filter(Boolean).join(" • ") || "N/A",
        ],
      );
    } else {
      summarySheet.push(["Cab", "No cab assigned"]);
    }

    summarySheet.push(
      [""],
      ["Performance Summary"],
      ["Total Trips", reportData.stats.totalTrips],
      ["Completed Trips", reportData.stats.completedTrips],
      ["Ongoing Trips", reportData.stats.ongoingTrips],
      ["Cancelled Trips", reportData.stats.cancelledTrips],
      ["Total Riders", reportData.stats.totalRiders],
      ["Average Riders per Trip", reportData.stats.averageRidersPerTrip],
      ["Average Occupancy (%)", reportData.stats.averageOccupancy],
      ["Reviews Received", reportData.stats.reviewCount],
      [
        "Average Rating",
        reportData.stats.averageRating != null ? reportData.stats.averageRating : "N/A",
      ],
      ["First Trip Date", formatDate(reportData.stats.firstTripDate)],
      ["Last Trip Date", formatDate(reportData.stats.lastTripDate)],
    );

    const summarySheetWs = XLSX.utils.aoa_to_sheet(summarySheet);
    XLSX.utils.book_append_sheet(wb, summarySheetWs, "Summary");

    const allTrips = (reportData.allTrips && reportData.allTrips.length > 0)
      ? reportData.allTrips
      : reportData.recentTrips;

    const tripsSheetData = [
      ["Trip ID", "Service Date", "State", "Start Location", "End Location", "Rider Count"],
      ...allTrips.map((trip) => [
        trip.id,
        formatDateTime(trip.serviceDate),
        trip.state,
        (trip as any).startLocationName || trip.startLocation || "-",
        (trip as any).endLocationName || trip.endLocation || "-",
        trip.riderCount,
      ]),
    ];

    const tripsSheetWs = XLSX.utils.aoa_to_sheet(tripsSheetData);
    XLSX.utils.book_append_sheet(wb, tripsSheetWs, "Trips");

    const allReviews = (reportData.allReviews && reportData.allReviews.length > 0)
      ? reportData.allReviews
      : reportData.reviews;

    const reviewsSheetData = [
      ["Review ID", "Rating", "Comment", "Author", "Created At"],
      ...allReviews.map((review) => [
        review.id,
        review.rating,
        review.comment || "-",
        review.giver?.name || "Anonymous",
        formatDateTime(review.createdAt),
      ]),
    ];

    const reviewsSheetWs = XLSX.utils.aoa_to_sheet(reviewsSheetData);
    XLSX.utils.book_append_sheet(wb, reviewsSheetWs, "Reviews");

    const filename = `driver-report-${safeName}-${new Date()
      .toISOString()
      .split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };


  // Edit Driver dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    email: "",
    plateNumber: "",
    capacity: "" as number | string,
    make: "",
    model: "",
    year: "" as number | string,
    color: "",
  });
  const [editFormError, setEditFormError] = useState<string | null>(null);

  // Block/Unblock confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"block" | "unblock">("block");
  const [confirmTarget, setConfirmTarget] = useState<UIDriverRow | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);


  const fetchDrivers = React.useCallback(async () => {
    let alive = true;
      setLoading(true);
      try {
        const res = await apiFetch(`/admin/users?page=1&limit=100&role=driver`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });
        const json: ApiResponse<ApiUser[]> = await res.json();
        // Debug: log users fetched from backend API as-is
        // console.log("[Admin] /admin/users raw data:", json?.data);
        if (json.success && Array.isArray(json.data)) {
          const ui: UIDriverRow[] = json.data.map((u) => {
            const cab = u.cab || null;
            const cabStatus = cab?.status ?? "-";
            const driverStatus = (u.status as UIDriverRow["driverStatus"]);
            return {
              id: u.id,
              name: u.name,
              email: u.email,
              driverStatus,
              cabPlate: cab?.plateNumber ?? "-",
              cabCapacity: cab?.capacity != null ? String(cab.capacity) : "-",
              cabStatus,
              cabMake: cab?.make ?? "-",
              cabModel: cab?.model ?? "-",
              cabYear: cab?.year != null ? String(cab.year) : "-",
              cabColor: cab?.color ?? "-",
            };
          });
          if (alive) setRows(ui);
        } else {
          if (alive) setRows([]);
        }
      } catch (e) {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
  }, [token]);

  useEffect(() => {
    let canceled = false;
    const run = async () => {
      if (!token) return;
      await fetchDrivers();
      if (canceled) return;
    };
    run();
    return () => {
      canceled = true;
    };
  }, [token, fetchDrivers]);

  

  const pill = (label: string, color: string) => (
    <Box component="span" sx={{
      display: "inline-block",
      px: 1,
      py: 0.25,
      borderRadius: 16,
      fontSize: 12,
      lineHeight: 1.4,
      fontWeight: 500,
      color: "#fff",
      backgroundColor: color,
    }}>{label}</Box>
  );


  const getDriverStatusPill = (s: UIDriverRow["driverStatus"]) => {
    const map: Record<UIDriverRow["driverStatus"], { label: string; color: string }> = {
      offline: { label: "Offline", color: "#f59e0b" }, // amber
      online: { label: "Online", color: "#10b981" },   // emerald
      in_trip: { label: "In Trip", color: "#14b8a6" }, // teal
      inactive: { label: "Inactive", color: "#ef4444" }, // red
    };
    const cfg = map[s] || { label: s, color: "#6b7280" };
    return pill(cfg.label, cfg.color);
  };

  const getCabStatusPill = (s: UIDriverRow["cabStatus"]) => {
    const map: Record<string, { label: string; color: string }> = {
      available: { label: "Available", color: "#10b981" },
      busy: { label: "Busy", color: "#14b8a6" },
      offline: { label: "Offline", color: "#f59e0b" },
      inactive: { label: "Inactive", color: "#ef4444" },
      "-": { label: "-", color: "#6b7280" },
    };
    const cfg = map[s] || { label: String(s), color: "#6b7280" };
    return pill(cfg.label, cfg.color);
  };

  const columns: GridColDef<UIDriverRow>[] = [
    { field: "name", headerName: "Driver", width: 150, flex: 0.8 },
    { field: "email", headerName: "Email", width: 200, flex: 1.2 },
    {
      field: "driverStatus",
      headerName: "Driver Status",
      width: 130,
      flex: 0.7,
      renderCell: (p) => getDriverStatusPill(p.value as UIDriverRow["driverStatus"]),
    },
    { field: "cabPlate", headerName: "Cab Plate", width: 120, flex: 0.6 },
    { field: "cabCapacity", headerName: "Capacity", width: 90, flex: 0.5 },
    { field: "cabMake", headerName: "Make", width: 100, flex: 0.6 },
    { field: "cabModel", headerName: "Model", width: 120, flex: 0.7 },
    { field: "cabYear", headerName: "Year", width: 80, flex: 0.5 },
    { field: "cabColor", headerName: "Color", width: 100, flex: 0.6 },
    {
      field: "cabStatus",
      headerName: "Cab Status",
      width: 120,
      flex: 0.7,
      renderCell: (p) => getCabStatusPill(p.value as UIDriverRow["cabStatus"]),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 190,
      flex: 0,
      align: 'center',
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View Report"
          onClick={() => handleViewDriverReport((params.row as UIDriverRow))}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => {
            const row = params.row as UIDriverRow;
            setEditForm({
              id: row.id,
              name: row.name,
              email: row.email,
              plateNumber: row.cabPlate === "-" ? "" : row.cabPlate,
              capacity: row.cabCapacity === "-" ? "" : Number(row.cabCapacity),
              make: row.cabMake === "-" ? "" : row.cabMake,
              model: row.cabModel === "-" ? "" : row.cabModel,
              year: row.cabYear === "-" ? "" : Number(row.cabYear),
              color: row.cabColor === "-" ? "" : row.cabColor,
            });
            setEditFormError(null);
            setEditOpen(true);
          }}
        />,
        (params.row as UIDriverRow).driverStatus === "inactive"
          ? (
              <GridActionsCellItem
                icon={<BlockIcon />}
                label="Unblock"
                onClick={() => {
                  setConfirmMode("unblock");
                  setConfirmTarget(params.row as UIDriverRow);
                  setConfirmError(null);
                  setConfirmOpen(true);
                }}
              />
            )
          : (
              <GridActionsCellItem
                icon={<BlockIcon />}
                label="Block"
                onClick={() => {
                  setConfirmMode("block");
                  setConfirmTarget(params.row as UIDriverRow);
                  setConfirmError(null);
                  setConfirmOpen(true);
                }}
              />
            ),
      ],
    },
  ];

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return rows.filter((u) => {
      const matchesSearch =
        (u.name || "").toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term);
      const matchesStatus = statusFilter === "all" || u.driverStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rows, searchTerm, statusFilter]);

  return (
    <Box>
      {/* Header Section with Gradient Background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
          color: 'white',
          p: 4,
          borderRadius: 2,
          mb: 3,
          boxShadow: '0 8px 32px rgba(20, 184, 166, 0.3)',
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Driver Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage drivers, vehicles, and their status
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setAddError(null);
                setAddForm({
                  name: "",
                  email: "",
                  licensePlate: "",
                  capacity: "",
                  make: "",
                  model: "",
                  year: "",
                  color: "",
                });
                setAddOtp("");
                setAddOtpRequested(false);
                setAddOpen(true);
              }}
            >
              Add Driver
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setRidersError(null);
                setRiderSingleForm({ name: "", email: "" });
                setRiderBulkForm({ namesText: "", emailsText: "" });
                setRidersOtp("");
                setRidersOtpRequested(false);
                setRidersMode('single');
                setRidersOpen(true);
              }}
            >
              Add Rider(s)
            </Button>
          </Box>
        </Box>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%)',
          border: '1px solid #10b98130',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flexGrow: 1, minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="in_trip">In Trip</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ height: 600, width: "100%", overflow: 'hidden' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          getRowId={(r) => r.id}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-columnHeader': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiDataGrid-cell--withRenderer': {
              overflow: 'visible',
            },
          }}
        />
      </Paper>

      {/* Driver Report Dialog */}
      <Dialog open={reportOpen} onClose={closeReportDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          {reportTarget ? `Driver Report - ${reportTarget.name}` : "Driver Report"}
          <IconButton
            aria-label="close"
            onClick={closeReportDialog}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => (theme as any).palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {reportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : reportError ? (
            <Typography color="error">{reportError}</Typography>
          ) : reportData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Driver Information
                  </Typography>
                  <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {reportData.driver.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {reportData.driver.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status: {reportData.driver.status}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Joined: {formatDate(reportData.driver.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cab Information
                  </Typography>
                  {reportData.cab ? (
                    <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Plate: {reportData.cab.plateNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Capacity: {reportData.cab.capacity ?? 'N/A'}
                      </Typography>
                      {reportData.cab.status && (
                        <Typography variant="body2" color="text.secondary">
                          Status: {reportData.cab.status}
                        </Typography>
                      )}
                      {(reportData.cab.make || reportData.cab.model) && (
                        <Typography variant="body2" color="text.secondary">
                          Vehicle: {[reportData.cab.make, reportData.cab.model].filter(Boolean).join(' ') || 'N/A'}
                        </Typography>
                      )}
                      {(reportData.cab.year || reportData.cab.color) && (
                        <Typography variant="body2" color="text.secondary">
                          Details: {[reportData.cab.year, reportData.cab.color].filter(Boolean).join(' • ') || 'N/A'}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No cab assigned.
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Performance Summary
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                  }}
                >
                  {statCard('Total Trips', reportData.stats.totalTrips.toLocaleString(), 'totalTrips')}
                  {statCard('Completed Trips', reportData.stats.completedTrips.toLocaleString(), 'completedTrips')}
                  {statCard('Ongoing Trips', reportData.stats.ongoingTrips.toLocaleString(), 'ongoingTrips')}
                  {statCard('Cancelled Trips', reportData.stats.cancelledTrips.toLocaleString(), 'cancelledTrips')}
                  {statCard('Total Riders', reportData.stats.totalRiders.toLocaleString(), 'totalRiders')}
                  {statCard('Avg Riders / Trip', reportData.stats.averageRidersPerTrip.toFixed(2), 'avgRiders')}
                  {statCard('Average Occupancy', `${reportData.stats.averageOccupancy}%`, 'avgOccupancy')}
                  {statCard('Reviews Received', reportData.stats.reviewCount.toLocaleString(), 'reviewCount')}
                  {statCard(
                    'Average Rating',
                    reportData.stats.averageRating != null ? `${reportData.stats.averageRating}/5` : 'N/A',
                    'avgRating'
                  )}
                  {statCard('First Trip', formatDate(reportData.stats.firstTripDate), 'firstTrip')}
                  {statCard('Last Trip', formatDate(reportData.stats.lastTripDate), 'lastTrip')}
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Recent Trips
                </Typography>
                {reportData.recentTrips.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No trips recorded yet.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {reportData.recentTrips.map((trip) => (
                      <Box
                        key={trip.id}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          p: 1.5,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatDateTime(trip.serviceDate)} • {trip.state.toUpperCase()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(trip.startLocationName || trip.startLocation || 'N/A') + ' → ' + (trip.endLocationName || trip.endLocation || 'N/A')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Riders: {trip.riderCount}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Latest Reviews
                </Typography>
                {reportData.reviews.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No reviews yet.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {reportData.reviews.map((review) => (
                      <Box
                        key={review.id}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          p: 1.5,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Rating: {review.rating}/5
                        </Typography>
                        {review.comment && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            “{review.comment}”
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {review.giver?.name ? `By ${review.giver.name}` : 'Anonymous'} • {formatDateTime(review.createdAt)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a driver to view their report.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleDownloadDriverReport}
            disabled={reportLoading || !reportData}
          >
            Download Report
          </Button>
          <Button onClick={closeReportDialog} disabled={reportLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Riders Dialog */}
      <Dialog open={ridersOpen} onClose={() => !ridersSubmitting && setRidersOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Add Rider(s)
          <IconButton
            aria-label="close"
            onClick={() => !ridersSubmitting && setRidersOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => (theme as any).palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!ridersOtpRequested && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Request an administrative OTP to authorize rider creation. It will be emailed to your admin account.
                </Typography>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!token) return;
                    setRidersRequestingOtp(true);
                    setRidersError(null);
                    try {
                      const res = await apiFetch('/admin/otp/request-create-rider', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({})
                      });
                      const data = await res.json();
                      if (!data.success) {
                        setRidersError(data.error || 'Failed to request OTP');
                      } else {
                        setRidersOtpRequested(true);
                        setRidersOtpCooldown(60);
                      }
                    } catch (e) {
                      setRidersError('Network error requesting OTP');
                    } finally {
                      setRidersRequestingOtp(false);
                    }
                  }}
                  disabled={ridersRequestingOtp}
                  endIcon={ridersRequestingOtp ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {ridersRequestingOtp ? 'Requesting OTP...' : 'Request Admin OTP'}
                </Button>
                {ridersError && <Box sx={{ color: 'error.main', fontSize: 13 }}>{ridersError}</Box>}
              </Box>
            )}
            {ridersOtpRequested && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <OtpInput
                    value={ridersOtp}
                    onChange={(otp) => setRidersOtp(otp)}
                    length={6}
                    disabled={ridersSubmitting}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      if (!token || ridersOtpCooldown > 0) return;
                      setRidersRequestingOtp(true);
                      setRidersError(null);
                      try {
                        const res = await apiFetch('/admin/otp/request-create-rider', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({})
                        });
                        const data = await res.json();
                        if (!data.success) {
                          setRidersError(data.error || 'Failed to resend OTP');
                        } else {
                          setRidersOtpCooldown(60);
                        }
                      } catch (e) {
                        setRidersError('Network error resending OTP');
                      } finally {
                        setRidersRequestingOtp(false);
                      }
                    }}
                    disabled={ridersRequestingOtp || ridersOtpCooldown > 0}
                  >
                    {ridersOtpCooldown > 0 ? `Resend OTP (${ridersOtpCooldown}s)` : 'Resend OTP'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">OTP authorizes this action.</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Mode:</Typography>
                  <Button
                    size="small"
                    variant={ridersMode === 'single' ? 'contained' : 'outlined'}
                    onClick={() => setRidersMode('single')}
                    disabled={ridersSubmitting}
                  >Single</Button>
                  <Button
                    size="small"
                    variant={ridersMode === 'bulk' ? 'contained' : 'outlined'}
                    onClick={() => setRidersMode('bulk')}
                    disabled={ridersSubmitting}
                  >Bulk</Button>
                </Box>
                {ridersMode === 'single' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Rider Details</Typography>
                    <TextField
                      label="Full Name"
                      value={riderSingleForm.name}
                      onChange={(e) => setRiderSingleForm((p) => ({ ...p, name: e.target.value }))}
                      required
                      fullWidth
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={riderSingleForm.email}
                      onChange={(e) => setRiderSingleForm((p) => ({ ...p, email: e.target.value }))}
                      required
                      fullWidth
                    />
                  </Box>
                )}
                {ridersMode === 'bulk' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Bulk Entry</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enter names and emails line-by-line. Line positions must align.
                    </Typography>
                    <TextField
                      label="Names (one per line)"
                      value={riderBulkForm.namesText}
                      onChange={(e) => setRiderBulkForm((p) => ({ ...p, namesText: e.target.value }))}
                      multiline minRows={4}
                      fullWidth
                    />
                    <TextField
                      label="Emails (one per line)"
                      value={riderBulkForm.emailsText}
                      onChange={(e) => setRiderBulkForm((p) => ({ ...p, emailsText: e.target.value }))}
                      multiline minRows={4}
                      fullWidth
                    />
                  </Box>
                )}
                {ridersError && <Box sx={{ color: 'error.main', fontSize: 13 }}>{ridersError}</Box>}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !ridersSubmitting && setRidersOpen(false)} disabled={ridersSubmitting}>Cancel</Button>
          {ridersOtpRequested && (
            <Button
              variant="contained"
              onClick={async () => {
                if (!ridersOtp || ridersOtp.trim().length < 6) { setRidersError('Enter 6-digit OTP'); return; }
                let ridersPayload: { name: string; email: string }[] = [];
                if (ridersMode === 'single') {
                  if (!riderSingleForm.name.trim() || !riderSingleForm.email.trim()) { setRidersError('Name & Email required'); return; }
                  ridersPayload.push({
                    name: riderSingleForm.name.trim(),
                    email: riderSingleForm.email.trim(),
                  });
                } else {
                  const names = riderBulkForm.namesText.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
                  const emailsRaw = riderBulkForm.emailsText.split(/\r?\n/).map(l => l.trim());
                  const emails = emailsRaw.filter(l => l.length > 0);
                  if (names.length === 0) { setRidersError('Provide at least one name'); return; }
                  if (emails.length !== names.length) { setRidersError('Names and Emails counts must match'); return; }
                  // Preserve alignment by index (ignore blank lines in both lists at same positions is complex; we simplified by filtering empties separately for now)
                  for (let i = 0; i < names.length; i++) {
                    const name = names[i];
                    const email = emails[i];
                    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setRidersError(`Invalid email at line ${i + 1}`); return; }
                    ridersPayload.push({ name, email });
                  }
                }
                if (ridersPayload.length === 0) { setRidersError('No riders to create'); return; }
                setRidersSubmitting(true);
                setRidersError(null);
                try {
                  const res = await apiFetch('/admin/riders', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                    body: JSON.stringify({ otp: ridersOtp.trim(), riders: ridersPayload }),
                  });
                  const data = await res.json();
                  if (!data.success) {
                    setRidersError(data.error || 'Failed to create riders');
                  } else {
                    // Success; close dialog.
                    setRidersOpen(false);
                  }
                } catch (e) {
                  setRidersError('Network error');
                } finally {
                  setRidersSubmitting(false);
                }
              }}
              disabled={ridersSubmitting}
              endIcon={ridersSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {ridersSubmitting ? (ridersMode === 'single' ? 'Creating...' : 'Creating Bulk...') : (ridersMode === 'single' ? 'Create Rider' : 'Create Riders')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Driver Dialog */}
      <Dialog open={addOpen} onClose={() => !addSubmitting && setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Add New Driver
          <IconButton
            aria-label="close"
            onClick={() => !addSubmitting && setAddOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => (theme as any).palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!addOtpRequested && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Request an administrative OTP to authorize driver creation. It will be emailed to your admin account.
                </Typography>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!token) return;
                    setAddRequestingOtp(true);
                    setAddError(null);
                    try {
                      const res = await apiFetch('/admin/otp/request-create-driver', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({}) 
                      });
                      const data = await res.json();
                      if (!data.success) {
                        setAddError(data.error || 'Failed to request OTP');
                      } else {
                        setAddOtpRequested(true);
                        setAddOtpCooldown(60);
                      }
                    } catch (e) {
                      setAddError('Network error requesting OTP');
                    } finally {
                      setAddRequestingOtp(false);
                    }
                  }}
                  disabled={addRequestingOtp}
                  endIcon={addRequestingOtp ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {addRequestingOtp ? 'Requesting OTP...' : 'Request Admin OTP'}
                </Button>
                {addError && <Box sx={{ color: 'error.main', fontSize: 13 }}>{addError}</Box>}
              </Box>
            )}
            {addOtpRequested && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <OtpInput
                    value={addOtp}
                    onChange={(otp) => setAddOtp(otp)}
                    length={6}
                    disabled={addSubmitting}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      if (!token || addOtpCooldown > 0) return;
                      setAddRequestingOtp(true);
                      setAddError(null);
                      try {
                        const res = await apiFetch('/admin/otp/request-create-driver', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({})
                        });
                        const data = await res.json();
                        if (!data.success) {
                          setAddError(data.error || 'Failed to resend OTP');
                        } else {
                          setAddOtpCooldown(60);
                        }
                      } catch (e) {
                        setAddError('Network error resending OTP');
                      } finally {
                        setAddRequestingOtp(false);
                      }
                    }}
                    disabled={addRequestingOtp || addOtpCooldown > 0}
                  >
                    {addOtpCooldown > 0 ? `Resend OTP (${addOtpCooldown}s)` : 'Resend OTP'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">OTP authorizes this action.</Typography>
                </Box>
                <Divider />
                <Typography variant="subtitle2" color="text.secondary">Driver Details</Typography>
                <TextField
                  label="Full Name"
                  value={addForm.name}
                  onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
                  required
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))}
                  required
                  fullWidth
                />
                <Divider />
                <Typography variant="subtitle2" color="text.secondary">Vehicle Information</Typography>
                <TextField
                  label="License Plate"
                  value={addForm.licensePlate}
                  onChange={(e) => setAddForm((p) => ({ ...p, licensePlate: e.target.value }))}
                  required
                  fullWidth
                />
                <TextField
                  label="Capacity"
                  type="number"
                  slotProps={{ htmlInput: { min: 1, max: 12 } }}
                  value={addForm.capacity}
                  onChange={(e) => setAddForm((p) => ({ ...p, capacity: e.target.value }))}
                  required
                  fullWidth
                />
                <TextField
                  label="Make"
                  value={addForm.make}
                  onChange={(e) => setAddForm((p) => ({ ...p, make: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Model"
                  value={addForm.model}
                  onChange={(e) => setAddForm((p) => ({ ...p, model: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Year"
                  type="number"
                  slotProps={{ htmlInput: { min: 1900, max: new Date().getFullYear() + 1 } }}
                  value={addForm.year}
                  onChange={(e) => setAddForm((p) => ({ ...p, year: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Color"
                  value={addForm.color}
                  onChange={(e) => setAddForm((p) => ({ ...p, color: e.target.value }))}
                  fullWidth
                />
                {addError && <Box sx={{ color: 'error.main', fontSize: 13 }}>{addError}</Box>}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !addSubmitting && setAddOpen(false)} disabled={addSubmitting}>Cancel</Button>
          {addOtpRequested && (
            <Button
              variant="contained"
              onClick={async () => {
                if (!addOtp || addOtp.trim().length < 6) { setAddError('Enter 6-digit OTP'); return; }
                if (!addForm.name || !addForm.email || !addForm.licensePlate || !addForm.capacity) { setAddError('Fill required fields'); return; }
                const capNum = parseInt(String(addForm.capacity), 10);
                if (!Number.isFinite(capNum) || capNum < 1) { setAddError('Capacity must be positive'); return; }
                let yearNum: number | undefined;
                if (addForm.year) {
                  yearNum = parseInt(String(addForm.year), 10);
                  if (!Number.isFinite(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear() + 1) { setAddError('Year invalid'); return; }
                }
                setAddSubmitting(true);
                setAddError(null);
                try {
                  const res = await apiFetch('/admin/drivers', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
                    body: JSON.stringify({
                      otp: addOtp.trim(),
                      name: addForm.name.trim(),
                      email: addForm.email.trim(),                      
                      vehicleInfo: {
                        licensePlate: addForm.licensePlate.trim(),
                        capacity: capNum,
                        make: addForm.make.trim() || undefined,
                        model: addForm.model.trim() || undefined,
                        year: yearNum,
                        color: addForm.color.trim() || undefined,
                      },
                    }),
                  });
                  const data = await res.json();
                  if (!data.success) {
                    setAddError(data.error || 'Failed to create driver');
                  } else {
                    await fetchDrivers();
                    setAddOpen(false);
                  }
                } catch (e) {
                  setAddError('Network error');
                } finally {
                  setAddSubmitting(false);
                }
              }}
              disabled={addSubmitting}
              endIcon={addSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {addSubmitting ? 'Creating...' : 'Create Driver'}
            </Button>
          )}
        </DialogActions>
      </Dialog>


      {/* Block/Unblock Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => !confirmSubmitting && setConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {confirmMode === 'block' ? 'Confirm Block Driver' : 'Confirm Unblock Driver'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {confirmMode === 'block'
              ? `Are you sure you want to mark ${(confirmTarget?.name) || 'this driver'} as inactive? Their cab will also be marked inactive and they will not receive trips.`
              : `Are you sure you want to unblock ${(confirmTarget?.name) || 'this driver'}? The driver and cab will move to Offline, and can go live again later.`}
          </Typography>
          {confirmError && (
            <Box sx={{ color: 'error.main', mt: 2, fontSize: 13 }}>{confirmError}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !confirmSubmitting && setConfirmOpen(false)} disabled={confirmSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmMode === 'block' ? 'error' : 'primary'}
            onClick={async () => {
              if (!confirmTarget) return;
              setConfirmSubmitting(true);
              setConfirmError(null);
              try {
                let res: Response;
                if (confirmMode === 'block') {
                  res = await apiFetch(`/admin/users/${confirmTarget.id}/status`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: token ? `Bearer ${token}` : '',
                    },
                    body: JSON.stringify({ status: 'inactive' }),
                  });
                } else {
                  // unblock -> set driver and cab to offline
                  res = await apiFetch(`/admin/users/${confirmTarget.id}/unblock`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: token ? `Bearer ${token}` : '',
                    },
                    body: JSON.stringify({}),
                  });
                }
                const data = await res.json();
                if (!data.success) {
                  setConfirmError(data.error || 'Operation failed.');
                } else {
                  await fetchDrivers();
                  setConfirmOpen(false);
                }
              } catch (e) {
                setConfirmError('Network error. Please try again.');
              } finally {
                setConfirmSubmitting(false);
              }
            }}
            disabled={confirmSubmitting}
            endIcon={confirmSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {confirmMode === 'block' ? (confirmSubmitting ? 'Blocking...' : 'Block') : (confirmSubmitting ? 'Unblocking...' : 'Unblock')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={editOpen} onClose={() => !editSubmitting && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Edit Driver
          <IconButton
            aria-label="close"
            onClick={() => !editSubmitting && setEditOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => (theme as any).palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Full Name"
              name="name"
              value={editForm.name}
              onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
              required
              fullWidth
            />
            <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 13 }}>
              Cab Details
            </Box>
            <TextField
              label="License Plate"
              name="plateNumber"
              value={editForm.plateNumber}
              onChange={(e) => setEditForm((p) => ({ ...p, plateNumber: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Make"
              name="make"
              value={editForm.make}
              onChange={(e) => setEditForm((p) => ({ ...p, make: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Model"
              name="model"
              value={editForm.model}
              onChange={(e) => setEditForm((p) => ({ ...p, model: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Year"
              name="year"
              type="number"
              slotProps={{ htmlInput: { min: 1900, max: new Date().getFullYear() + 1 } }}
              value={editForm.year}
              onChange={(e) => setEditForm((p) => ({ ...p, year: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Color"
              name="color"
              value={editForm.color}
              onChange={(e) => setEditForm((p) => ({ ...p, color: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Capacity"
              name="capacity"
              type="number"
              slotProps={{ htmlInput: { min: 1, max: 12 } }}
              value={editForm.capacity}
              onChange={(e) => setEditForm((p) => ({ ...p, capacity: e.target.value }))}
              required
              fullWidth
            />
            {editFormError && (
              <Box sx={{ color: 'error.main', fontSize: 13 }}>{editFormError}</Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !editSubmitting && setEditOpen(false)} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              // Basic validation
              if (!editForm.id || !editForm.name || !editForm.email || !editForm.plateNumber) {
                setEditFormError("Please fill all required fields.");
                return;
              }
              const capacityNum = parseInt(String(editForm.capacity), 10);
              if (!Number.isFinite(capacityNum) || capacityNum < 1) {
                setEditFormError("Capacity must be a positive number.");
                return;
              }

              setEditSubmitting(true);
              setEditFormError(null);
              try {
                const res = await apiFetch(`/admin/users/${editForm.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : '',
                  },
                  body: JSON.stringify({
                    name: editForm.name.trim(),
                    email: editForm.email.trim(),
                    cab: {
                      plateNumber: editForm.plateNumber.trim(),
                      capacity: capacityNum,
                      make: editForm.make.trim() || null,
                      model: editForm.model.trim() || null,
                      year: editForm.year ? Number(editForm.year) : null,
                      color: editForm.color.trim() || null,
                    },
                  }),
                });
                const data = await res.json();
                if (!data.success) {
                  setEditFormError(data.error || 'Failed to update driver.');
                } else {
                  await fetchDrivers();
                  setEditOpen(false);
                }
              } catch (e) {
                setEditFormError('Network error. Please try again.');
              } finally {
                setEditSubmitting(false);
              }
            }}
            disabled={editSubmitting}
            endIcon={editSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      
    </Box>
  );
};

export default Users;
